import { useCallback, useEffect, useRef } from 'react';
import { requiresSafariHack } from '../../helper/isSSR';
const useSwiffySlider = ({ slides, nodelay, noloop, autoplay, autopause, autoplayInterval, }) => {
    const containerRef = useRef(null);
    const indicatorsRef = useRef(null);
    const slidesLength = slides.length;
    const clonedSlides = slides.length > 1 && !noloop ? [slides[slidesLength - 1], ...slides, slides[0]] : slides;
    const slideTo = useCallback((slideIndex, forceSlide) => {
        const { current: container } = containerRef;
        if (!container) {
            return;
        }
        let realIndex = slideIndex;
        if (forceSlide) {
            container.scroll({
                left: container.children[realIndex].offsetLeft,
                behavior: 'instant',
            });
            return;
        }
        if (!noloop)
            ++realIndex;
        container.scroll({
            left: container.children[realIndex].offsetLeft,
            behavior: nodelay ? 'auto' : 'smooth',
        });
    }, [nodelay, noloop]);
    const slide = useCallback((next = true) => {
        const { current: container } = containerRef;
        if (!container) {
            return;
        }
        const children = container.children;
        const active = container.querySelector('[data-visible]');
        let index = active ? Array.from(children).indexOf(active) : 0;
        if (noloop) {
            if (next) {
                if (++index > slidesLength)
                    index = 0;
            }
            else if (--index < 0)
                index = slidesLength - 1;
        }
        else if (!next)
            index -= 2; // slideTo does ++index
        slideTo(index);
    }, [noloop, slideTo, slidesLength]);
    useEffect(() => {
        const { current: container } = containerRef;
        if (!container) {
            return undefined;
        }
        let interval;
        const autoPlay = () => {
            interval = setInterval(() => slide(), autoplayInterval);
        };
        const stopAutoplay = () => {
            window.clearTimeout(interval);
        };
        if (autoplay) {
            const observer = new IntersectionObserver(entries => {
                if (entries[0].isIntersecting) {
                    autoPlay();
                }
                else {
                    stopAutoplay();
                }
            });
            observer.observe(container);
            let removeFn;
            if (autopause) {
                ['mouseover', 'touchstart'].forEach(event => {
                    container.addEventListener(event, stopAutoplay);
                });
                ['mouseout', 'touchend'].forEach(event => {
                    container.addEventListener(event, autoPlay);
                });
                removeFn = () => {
                    ['mouseover', 'touchstart'].forEach(event => {
                        container.removeEventListener(event, stopAutoplay);
                    });
                    ['mouseout', 'touchend'].forEach(event => {
                        container.removeEventListener(event, autoPlay);
                    });
                };
            }
            return () => {
                stopAutoplay();
                observer.disconnect();
                removeFn?.();
            };
        }
        return stopAutoplay;
    }, [autopause, autoplay, autoplayInterval, slide]);
    const init = useCallback(() => {
        const { current: container } = containerRef;
        const { current: indicatorsParent } = indicatorsRef;
        if (!container || !indicatorsParent) {
            return;
        }
        const children = Array.from(container.children);
        const len = children.length - 1;
        const indicators = Array.from(indicatorsParent.children);
        const updateIndicators = (index) => {
            let realIndex = index;
            if (!noloop) {
                // adjust for cloned slides
                if (realIndex === 0)
                    realIndex = slidesLength - 1;
                else if (realIndex > slidesLength)
                    realIndex = 0;
                else
                    --realIndex;
            }
            for (const child of indicators)
                child.removeAttribute('data-active');
            indicators[realIndex].setAttribute('data-active', '1');
        };
        // update navigation and slide style
        const navObserver = new IntersectionObserver(nodes => {
            let activeNode = null;
            for (let i = 0; i < nodes.length; ++i) {
                const node = nodes[i];
                if (!node.isIntersecting)
                    continue;
                activeNode = node.target;
                updateIndicators(children.indexOf(activeNode));
                break;
            }
            if (!activeNode)
                return; // this shouldn't be happening.
            for (const child of children)
                child.removeAttribute('data-visible');
            activeNode.setAttribute('data-visible', '1');
        }, { root: container, threshold: 0.51 });
        let animationFrame = 0;
        const peekObserver = noloop
            ? undefined
            : new IntersectionObserver(nodes => {
                let activeNode = null;
                for (let i = 0; i < nodes.length; ++i) {
                    const node = nodes[i];
                    if (!node.isIntersecting)
                        continue;
                    activeNode = node.target;
                    break;
                }
                if (!activeNode)
                    return; // nothing to handle, we've scrolled over it already
                // we're using a trick here: we have cloned the 1st and the last slide earlier
                // so when a user reaches one of the cloned slides, we scroll them to the real slide.
                // with threshold: 1, it means we do the scroll exactly when the slide became fully visible
                // compared to threshold: 0, this allows users to peek (left or right slide).
                activeNode = activeNode === children[0] ? children[len - 1] : children[1];
                cancelAnimationFrame(animationFrame);
                animationFrame = requestAnimationFrame(() => {
                    const offset = activeNode.offsetLeft;
                    container.scroll({
                        left: offset,
                        behavior: 'instant',
                    });
                });
            }, {
                root: container,
                threshold: 1,
                // we use margin left here, because in iOS Safari,
                // when a user slides and holds a slide and does not let go,
                // the observer does not trigger until all of the slide is visible.
                rootMargin: requiresSafariHack ? '0px 25% 0px 25%' : undefined,
            });
        if (!noloop) {
            peekObserver?.observe(children[0]);
            peekObserver?.observe(children[len]);
        }
        // eslint-disable-next-line no-restricted-syntax
        for (const child of children)
            navObserver.observe(child);
        // eslint-disable-next-line consistent-return
        return () => {
            navObserver.disconnect();
            peekObserver?.disconnect();
        };
    }, [noloop, slidesLength]);
    useEffect(() => {
        const cleanup = init();
        if (noloop)
            return cleanup;
        const container = containerRef.current;
        if (!container)
            return cleanup;
        // for SSR, we don't want to show the cloned last slide 1st, so we hide it until we're ready.
        requestAnimationFrame(() => {
            container.setAttribute('data-init', 'true');
            slideTo(1, true);
        });
        return cleanup;
    }, [init, noloop, slideTo]);
    return {
        containerRef,
        indicatorsRef,
        slide,
        slideTo,
        slidesLength,
        clonedSlides,
    };
};
export { useSwiffySlider };
